import React, {useEffect, useState} from "react";
import {useTranslate} from "@tolgee/react";
import useSearchParam from "hooks/useSearchParam";
import {apiV2} from "api/firebase";
import {getErrorMessage} from "tools/error";
import KeyIcon from "view/images/KeyIcon";
import FlexBox from "view/components/Layout/FlexBox";
import {CircularProgress, Typography} from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import {useForm} from "react-hook-form";
import Alert from "view/components/Alert";
import Form, {ActionButton} from "view/components/Form";
import {useNavigate} from "react-router-dom";
import {LOGIN_PATH} from "view/routes";
import {red} from "@material-ui/core/colors";
import PasswordInputs from "view/components/PasswordInputs";
import PublicLanguagePicker from "view/components/LanguagePicker/PublicLanguagePicker";
import Box from "@material-ui/core/Box";
import {jwtDecode} from "jwt-decode";
import Copyright from "view/components/Copyright";
import useStyles from "./styles";

enum TokenVerificationStatus {
  Pending = "pending",
  Failed = "failed",
  Succeeded = "succeeded",
}
type PasswordResetFormReturn = {
  password: string;
  confirmPassword: string;
};

const EXPIRED_LINK_ERROR = "PASSWORD_RESET_EXPIRED_LINK";
const MISSING_TOKEN_ERROR = "PASSWORD_RESET_MISSING_TOKEN";

const ERROR_KEYS: Record<string, string> = {
  [EXPIRED_LINK_ERROR]: "global.password_reset.link_expired",
  [MISSING_TOKEN_ERROR]: "global.password_reset.missing_token",
};

const PasswordReset = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const {t} = useTranslate();

  const token = useSearchParam("token");
  const [error, setError] = useState("");
  const [
    tokenVerificationStatus,
    setTokenVerificationStatus,
  ] = useState<TokenVerificationStatus>(TokenVerificationStatus.Pending);

  useEffect(() => {
    setError("");
    if (!token) {
      return setError(MISSING_TOKEN_ERROR);
    }

    const verifyToken = async () => {
      try {
        const {exp} = jwtDecode(token);
        const nowSeconds = Math.floor(Date.now() / 1000);

        if (!exp || exp < nowSeconds) {
          throw new Error(EXPIRED_LINK_ERROR);
        }

        await apiV2.auth.verifyPasswordResetToken({token});
        setTokenVerificationStatus(TokenVerificationStatus.Succeeded);
      } catch (e) {
        setTokenVerificationStatus(TokenVerificationStatus.Failed);
        console.error("Error occurred verifying token: ", e);
        setError(getErrorMessage(e));
      }
    };

    verifyToken();
  }, [token]);

  const form = useForm<PasswordResetFormReturn>({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    mode: "all",
  });

  const {
    handleSubmit,
    formState: {isSubmitSuccessful},
  } = form;

  const onSubmit = async ({password}: PasswordResetFormReturn) => {
    if (!token) {
      return;
    }
    try {
      if (error) {
        setError("");
      }

      await apiV2.auth.resetPassword({password, token});
    } catch (e) {
      console.error("Error occurred updating password: ", e);
      setError(getErrorMessage(e));
    }
  };

  const renderContent = () => {
    if (error) {
      return (
        <FlexBox gap={1} alignCenter justifyCenter>
          <CancelIcon htmlColor={red[500]} />
          <Typography variant="h4" color="error">
            {ERROR_KEYS[error] ? t(ERROR_KEYS[error]) : error}
          </Typography>
        </FlexBox>
      );
    }
    if (isSubmitSuccessful) {
      return (
        <Alert severity="success">{t("global.password_reset.success")}</Alert>
      );
    }
    if (tokenVerificationStatus === TokenVerificationStatus.Pending) {
      return (
        <FlexBox alignCenter justifyCenter>
          <CircularProgress size={24} />
        </FlexBox>
      );
    }
    return <PasswordInputs t={t} />;
  };

  const navigateToLogin = () => navigate(LOGIN_PATH);

  const renderActions = () => {
    if (tokenVerificationStatus === TokenVerificationStatus.Pending) {
      return null;
    }
    const isSingleAction = !!error || isSubmitSuccessful;

    const backText = isSubmitSuccessful
      ? t("global.password_reset.back_to_sign_in")
      : t("global.password_reset.back_to_login_page");
    const submitText = isSingleAction ? backText : t("common.action.submit");

    return (
      <FlexBox justifyBetween={!isSingleAction} justifyCenter={isSingleAction}>
        {!isSingleAction && (
          <ActionButton
            text={t("global.login.remember_password")}
            variant="text"
            color="primary"
            style={{textDecoration: "underline"}}
            onClick={navigateToLogin}
          />
        )}
        <ActionButton
          text={submitText}
          type={isSingleAction ? "button" : "submit"}
          variant="contained"
          color="primary"
          onClick={isSingleAction ? navigateToLogin : undefined}
        />
      </FlexBox>
    );
  };

  return (
    <>
      <Box className={classes.root}>
        <FlexBox column gap={3} className={classes.content}>
          <FlexBox column gap={2} fullWidth justifyCenter alignCenter>
            <KeyIcon />
            <Typography variant="h2" className={classes.title}>
              {t("global.password_reset.title")}
            </Typography>
          </FlexBox>
          <Form
            form={form}
            onSubmit={handleSubmit(onSubmit)}
            noDefaultStyles
            className={classes.form}
          >
            {renderContent()}
            {renderActions()}
          </Form>
        </FlexBox>
      </Box>
      <FlexBox column gap={1} className={classes.copyright}>
        <PublicLanguagePicker />
        <Copyright />
      </FlexBox>
    </>
  );
};

export default PasswordReset;
